import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  CheckCircle2, ChevronDown, AlertCircle, Plus, X, Save, Send, Clock3,
  ArrowLeft, ClipboardList, Wallet, FileText, Sparkles, Megaphone, Eye, BadgeCheck, MapPin, Briefcase,
  BarChart3, Laptop, Info, CheckCheck, MessageSquareText, Trash2,
} from 'lucide-react'
import {
  getJobCategories, getEnums, getMyJobDetail, createJob, updateMyJob, setMyJobStatus, parseJsonList,
  getSkillTags, SCREENING_TYPE_LABELS,
  type ApiJobCategory, type ApiSkillTag, type ScreeningQuestion, type CreateJobPayload,
} from '@/services/jobService'
import { getProfile, type CompanyProfile } from '@/services/profileService'
import applyBg from '@/pages/JobApplication/assets/apply-bg.png'
import CompanyLogo from '@/components/ui/CompanyLogo'

// โหมดแก้ไข: เปิดจากปุ่ม "แก้ไข" ในหน้าประกาศงานของบริษัท (JobPostingsTab) ด้วย /post-job?edit=<job_id>
// โหลดข้อมูลเต็มจาก GET /api/my-jobs/:id (ได้ทุกสถานะ) และบันทึกด้วย PUT /api/my-jobs/:id — มีผลทันที

// ── Types (mirrors m_job columns only — no invented fields) ───────────────────
// ค่าจริงของ WorkType/ExperienceLevel ดึงจาก GET /api/enums เสมอ (ห้าม hardcode) — type
// string ธรรมดาไว้พอเป็น placeholder ตอน initial state เท่านั้น
type WorkType = string
type ExperienceLevel = string
type JobStatus = 'DRAFT' | 'ACTIVE'

interface JobForm {
  title: string
  category_id: string
  work_type: WorkType
  experience_level: ExperienceLevel
  location: string
  is_remote: boolean
  salary_min: string
  salary_max: string
  currency: string
  description: string
  requirements: string[]
  nice_to_have: string[]
  benefits: string[]   // สวัสดิการ → m_job.benefits
  tag_ids: number[]   // ทักษะที่ต้องการ — เลือกจาก m_skill_tag ที่มีอยู่แล้ว (ส่งเป็น tag_ids ให้ createJob ตรงๆ)
  is_urgent: boolean
  status: JobStatus
  expires_at: string
  screening_questions: QuestionDraft[]   // คำถามเพิ่มเติมถึงผู้สมัคร → m_job.screening_questions
}

// คำถามเพิ่มเติม 1 ข้อระหว่างแก้ในฟอร์ม — key ใช้เป็น React key เท่านั้น (id จริงให้ server ออก)
type ScreeningType = ScreeningQuestion['type']
interface QuestionDraft {
  key: number
  id?: string
  type: ScreeningType
  question: string
  required: boolean
  options: string[]
}

const MAX_SCREENING_QUESTIONS = 3
const MIN_CHOICE_OPTIONS = 2
const MAX_CHOICE_OPTIONS = 6

const INITIAL_FORM: JobForm = {
  title: '', category_id: '', work_type: 'FULL_TIME', experience_level: 'MID',
  location: '', is_remote: false, salary_min: '', salary_max: '', currency: 'THB',
  description: '', requirements: [], nice_to_have: [], benefits: [], tag_ids: [],
  is_urgent: false, status: 'ACTIVE', expires_at: '', screening_questions: [],
}

// ตรวจคำถามเพิ่มเติมก่อนส่ง (backend ตรวจซ้ำอีกชั้น) — คืนข้อความ error แรกที่เจอ หรือ '' ถ้าผ่าน
function validateQuestions(questions: QuestionDraft[]): string {
  for (const [i, q] of questions.entries()) {
    if (!q.question.trim()) return `คำถามข้อ ${i + 1}: กรุณากรอกคำถาม`
    if (q.type === 'CHOICE') {
      const opts = new Set(q.options.map(o => o.trim()).filter(Boolean))
      if (opts.size < MIN_CHOICE_OPTIONS) return `คำถามข้อ ${i + 1}: ต้องมีตัวเลือกอย่างน้อย ${MIN_CHOICE_OPTIONS} ตัว (ไม่ซ้ำกัน)`
    }
  }
  return ''
}

function toScreeningPayload(questions: QuestionDraft[]): ScreeningQuestion[] {
  return questions.map(q => ({
    ...(q.id ? { id: q.id } : {}),
    type: q.type,
    question: q.question.trim(),
    required: true,   // ผู้สมัครต้องตอบทุกข้อ
    ...(q.type === 'CHOICE' ? { options: q.options.map(o => o.trim()).filter(Boolean) } : {}),
  }))
}


// label ภาษาไทยไว้แค่แปลค่าที่ดึงมาจาก /api/enums ให้อ่านง่ายขึ้น — ไม่ใช่ตัวกำหนดว่ามีค่าอะไรบ้าง
const WORK_TYPE_LABELS: Record<string, string> = {
  FULL_TIME: 'งานประจำ', PART_TIME: 'พาร์ทไทม์', REMOTE: 'รีโมท',
  HYBRID: 'ไฮบริด', FREELANCE: 'ฟรีแลนซ์', INTERNSHIP: 'ฝึกงาน',
}
const EXP_LABELS: Record<string, string> = {
  ENTRY: 'Entry Level', MID: 'Mid Level', SENIOR: 'Senior', LEAD: 'Lead / Manager', C_LEVEL: 'C-Level / Executive',
}
const CURRENCIES = ['THB', 'USD', 'SGD']

// ── Shared building blocks ─────────────────────────────────────────────────────
type IconType = React.ComponentType<{ className?: string }>

function SectionHeading({ icon: Icon, title, description }: {
  icon: IconType; title: string; description?: string
}) {
  return (
    <div className="mb-6 flex items-start gap-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-sm bg-accent/10 text-accent">
        <Icon className="h-[18px] w-[18px]" />
      </div>
      <div>
        <h2 className="text-base font-bold text-text">{title}</h2>
        {description && <p className="mt-0.5 text-xs text-text-muted">{description}</p>}
      </div>
    </div>
  )
}

function SubHeading({ icon: Icon, title }: { icon: IconType; title: string }) {
  return (
    <div className="mb-3 flex items-center gap-2">
      <Icon className="h-4 w-4 text-accent" />
      <h3 className="text-sm font-bold text-text">{title}</h3>
    </div>
  )
}

function Divider() {
  return <div className="h-px w-full bg-[#E2E4EA]" />
}

function Field({ label, required, hint, error, children }: {
  label: string; required?: boolean; hint?: string; error?: string; children: React.ReactNode
}) {
  return (
    <div>
      <label className="mb-1.5 block text-[13px] font-medium text-text">
        {label}{required && <span className="ml-1 text-rose-500">*</span>}
      </label>
      {children}
      {error ? (
        <p className="mt-1.5 text-xs text-rose-500">{error}</p>
      ) : hint ? (
        <p className="mt-1.5 text-xs text-text-muted/80">{hint}</p>
      ) : null}
    </div>
  )
}

const inputBase = 'w-full rounded-sm border border-[#E2E4EA] bg-white px-3.5 text-sm text-text placeholder-text-muted/60 transition-colors focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent/25'

function Input({ className = '', ...props }: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input className={`${inputBase} h-[46px] ${className}`} {...props} />
}

function Select({ children, className = '', ...props }: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <div className="relative">
      <select className={`${inputBase} h-[46px] appearance-none pr-9 ${className}`} {...props}>{children}</select>
      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
    </div>
  )
}

function Textarea({ className = '', ...props }: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={`${inputBase} resize-none py-3 leading-relaxed ${className}`} {...props} />
}

// Simple horizontal setting row (used in Publishing section)
function SettingRow({ label, description, children }: {
  label: string; description?: string; children: React.ReactNode
}) {
  return (
    <div className="flex items-center justify-between gap-6 py-4">
      <div>
        <p className="text-sm font-medium text-text">{label}</p>
        {description && <p className="mt-0.5 text-xs text-text-muted">{description}</p>}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  )
}

function Switch({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <button
      type="button"
      onClick={onChange}
      role="switch"
      aria-checked={checked}
      className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${checked ? 'bg-accent' : 'bg-[#DBDCE3]'}`}
    >
      <span className={`absolute top-0.5 left-0.5 inline-block h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${checked ? 'translate-x-5' : 'translate-x-0'}`} />
    </button>
  )
}

// Compact segmented option group — replaces pill buttons
function SegmentedGroup<T extends string>({ options, value, onChange }: {
  options: { value: T; label: string }[]; value: T; onChange: (v: T) => void
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(opt => {
        const active = opt.value === value
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={`rounded-sm border px-3.5 py-2 text-[13px] font-medium transition-colors ${
              active
                ? 'border-[#7567FF] bg-[#F3F1FF] text-accent'
                : 'border-[#E2E4EA] bg-white text-text-muted hover:border-[#C7C9D6] hover:text-text'
            }`}
          >
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}

// Dynamic add/remove list (used for requirements)
function DynamicList({ items, onAdd, onRemove, placeholder }: {
  items: string[]; onAdd: (v: string) => void; onRemove: (i: number) => void; placeholder?: string
}) {
  const [val, setVal] = useState('')
  const add = () => { if (val.trim()) { onAdd(val.trim()); setVal('') } }
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input
          value={val} onChange={e => setVal(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), add())}
          placeholder={placeholder || 'พิมพ์แล้วกด Enter เพื่อเพิ่ม...'}
          className={`${inputBase} h-[46px] flex-1`}
        />
        <button
          type="button"
          onClick={add}
          aria-label="เพิ่มคุณสมบัติ"
          className="flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-sm border border-[#E2E4EA] bg-white text-text-muted transition-colors hover:border-accent/50 hover:text-accent"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
      {items.length > 0 && (
        <ul className="divide-y divide-[#E2E4EA] rounded-sm border border-[#E2E4EA]">
          {items.map((item, i) => (
            <li key={i} className="flex items-center gap-2.5 px-3.5 py-2.5">
              <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-accent" />
              <span className="flex-1 text-sm text-text">{item}</span>
              <button type="button" onClick={() => onRemove(i)} className="text-text-muted transition-colors hover:text-rose-500">
                <X className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// Toggle-chip multi-select for skill tags (ทักษะที่ต้องการ) — ข้อมูลจริงจาก /api/skill-tags (m_skill_tag)
function SkillPicker({ options, selected, onToggle }: {
  options: ApiSkillTag[]; selected: number[]; onToggle: (tagId: number) => void
}) {
  if (options.length === 0) return <p className="text-sm text-text-muted">กำลังโหลดรายการทักษะ...</p>
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(tag => {
        const active = selected.includes(tag.tag_id)
        return (
          <button
            key={tag.tag_id}
            type="button"
            onClick={() => onToggle(tag.tag_id)}
            className={`flex items-center gap-1.5 rounded-sm border px-3 py-1.5 text-[13px] font-medium transition-colors ${
              active
                ? 'border-[#7567FF] bg-[#F3F1FF] text-accent'
                : 'border-[#E2E4EA] bg-white text-text-muted hover:border-[#C7C9D6] hover:text-text'
            }`}
          >
            {active && <CheckCheck className="h-3.5 w-3.5" />}
            {tag.name}
          </button>
        )
      })}
    </div>
  )
}

// ── คำถามเพิ่มเติมถึงผู้สมัคร (screening questions) — ผู้สมัครตอบในขั้น "ตอบคำถาม" ของใบสมัคร ──
function ScreeningQuestionsEditor({ questions, typeOptions, onChange }: {
  questions: QuestionDraft[]
  typeOptions: ScreeningType[]
  onChange: (questions: QuestionDraft[]) => void
}) {
  const nextKey = () => questions.reduce((max, q) => Math.max(max, q.key), 0) + 1
  const update = (i: number, patch: Partial<QuestionDraft>) =>
    onChange(questions.map((q, idx) => (idx === i ? { ...q, ...patch } : q)))
  const add = () => onChange([...questions, {
    key: nextKey(), type: 'YES_NO', question: '', required: false, options: ['', ''],
  }])
  const remove = (i: number) => onChange(questions.filter((_, idx) => idx !== i))

  return (
    <div className="space-y-4">
      {questions.map((q, i) => (
        <div key={q.key} className="rounded-sm border border-[#E2E4EA] p-4">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-xs font-semibold text-text-muted">คำถามข้อ {i + 1}</p>
            <button type="button" onClick={() => remove(i)} aria-label="ลบคำถาม"
              className="text-text-muted transition-colors hover:text-rose-500">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>

          <div className="space-y-3">
            <Input value={q.question} onChange={e => update(i, { question: e.target.value })}
              maxLength={300} placeholder="เช่น มีใบขับขี่รถโฟล์คลิฟท์หรือไม่" />

            <SegmentedGroup
              options={typeOptions.map(value => ({ value, label: SCREENING_TYPE_LABELS[value] ?? value }))}
              value={q.type}
              onChange={v => update(i, { type: v })}
            />

            {q.type === 'CHOICE' && (
              <div className="space-y-2">
                {q.options.map((opt, oi) => (
                  <div key={oi} className="flex gap-2">
                    <Input value={opt} maxLength={100} placeholder={`ตัวเลือกที่ ${oi + 1}`}
                      onChange={e => update(i, { options: q.options.map((o, idx) => (idx === oi ? e.target.value : o)) })} />
                    {q.options.length > MIN_CHOICE_OPTIONS && (
                      <button type="button" aria-label="ลบตัวเลือก"
                        onClick={() => update(i, { options: q.options.filter((_, idx) => idx !== oi) })}
                        className="flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-sm border border-[#E2E4EA] text-text-muted transition-colors hover:text-rose-500">
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
                {q.options.length < MAX_CHOICE_OPTIONS && (
                  <button type="button" onClick={() => update(i, { options: [...q.options, ''] })}
                    className="flex items-center gap-1.5 text-xs font-medium text-accent hover:text-accent2">
                    <Plus className="h-3.5 w-3.5" /> เพิ่มตัวเลือก
                  </button>
                )}
              </div>
            )}

          </div>
        </div>
      ))}

      {questions.length < MAX_SCREENING_QUESTIONS ? (
        <button type="button" onClick={add}
          className="flex items-center gap-1.5 rounded-sm border border-dashed border-accent/40 px-4 py-2.5 text-sm font-medium text-accent transition-colors hover:bg-accent/5">
          <Plus className="h-4 w-4" /> เพิ่มคำถาม
        </button>
      ) : (
        <p className="text-xs text-text-muted">เพิ่มได้สูงสุด {MAX_SCREENING_QUESTIONS} ข้อ</p>
      )}
    </div>
  )
}

// ── Live job preview (ขวา) — สะท้อนค่าจาก form state จริงแบบเรียลไทม์ ──────────────
function JobPreviewPanel({ company, form, categoryName, skillTags }: {
  company: CompanyProfile | null
  form: JobForm
  categoryName: string
  skillTags: ApiSkillTag[]
}) {
  const selectedSkills = skillTags.filter(t => form.tag_ids.includes(t.tag_id))
  const salaryText = form.salary_min || form.salary_max
    ? `฿${form.salary_min ? Number(form.salary_min).toLocaleString() : ''}${form.salary_min && form.salary_max ? ' – ' : ''}${form.salary_max ? Number(form.salary_max).toLocaleString() : ''} ${form.currency}/เดือน`
    : null

  return (
    <div className="rounded-sm border border-[#E2E4EA] bg-white p-6">
      <div className="mb-5 flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm bg-accent/10 text-accent">
            <Eye className="h-4 w-4" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-text">ตัวอย่างประกาศ</h3>
            <p className="text-[11px] text-text-muted">ข้อมูลจะอัปเดตตามที่คุณกรอกแบบฟอร์ม</p>
          </div>
        </div>
      </div>

      {/* Job card preview */}
      <div className="flex items-center gap-3">
        <CompanyLogo name={company?.company_name || ''} logoUrl={company?.logo_url} size={44} className="rounded-sm" />
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="truncate text-sm font-bold text-text">{company?.company_name || 'บริษัทของคุณ'}</p>
            {company?.is_verified && <BadgeCheck className="h-3.5 w-3.5 shrink-0 text-blue-400" />}
          </div>
          <p className="truncate text-xs text-text-muted">{categoryName || company?.industry || '—'}</p>
        </div>
      </div>

      <h4 className="mt-3 text-lg font-bold leading-snug text-text">
        {form.title || 'ชื่อตำแหน่งงานของคุณ'}
      </h4>

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-text-muted">
        <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {form.location || 'สถานที่ทำงาน'}</span>
        <span className="flex items-center gap-1"><Briefcase className="h-3.5 w-3.5" /> {WORK_TYPE_LABELS[form.work_type] ?? form.work_type}</span>
        <span className="flex items-center gap-1"><BarChart3 className="h-3.5 w-3.5" /> {EXP_LABELS[form.experience_level] ?? form.experience_level}</span>
        {form.is_remote && <span className="flex items-center gap-1"><Laptop className="h-3.5 w-3.5" /> Remote</span>}
      </div>

      <p className="mt-3 text-base font-bold text-emerald-500">
        {salaryText || <span className="text-sm font-medium text-text-muted">ไม่ระบุเงินเดือน</span>}
      </p>

      {selectedSkills.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {selectedSkills.slice(0, 4).map(s => (
            <span key={s.tag_id} className="rounded-sm border border-[#E7E5FF] bg-[#F8F7FF] px-2.5 py-1 text-xs text-text">{s.name}</span>
          ))}
          {selectedSkills.length > 4 && (
            <span className="rounded-sm border border-[#E7E5FF] bg-[#F8F7FF] px-2.5 py-1 text-xs text-text-muted">+{selectedSkills.length - 4}</span>
          )}
        </div>
      )}

      <Divider />
      <div className="py-4">
        <p className="mb-1.5 text-xs font-semibold text-text">เกี่ยวกับตำแหน่งงาน</p>
        <p className="line-clamp-3 text-xs leading-relaxed text-text-muted">
          {form.description || 'รายละเอียดงานจะแสดงที่นี่ เมื่อคุณกรอกข้อมูลในขั้นตอน "รายละเอียดงาน"'}
        </p>
      </div>

      {form.screening_questions.length > 0 && (
        <p className="flex items-center gap-1.5 text-xs text-text-muted">
          <MessageSquareText className="h-3.5 w-3.5 text-accent" />
          ผู้สมัครต้องตอบคำถามเพิ่มเติม {form.screening_questions.length} ข้อ
        </p>
      )}

      <div className="mt-5 flex items-start gap-2 rounded-sm bg-[#F8F7FF] px-3.5 py-3 text-[11px] leading-relaxed text-text-muted">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent" />
        นี่เป็นเพียงตัวอย่างการแสดงผล ข้อมูลจริงอาจแตกต่างออกไปตามเนื้อหาที่คุณกรอก
      </div>
    </div>
  )
}

// ── Result screens ──────────────────────────────────────────────────────────────
function DraftSavedScreen({ title, onContinue, onNew, onGoList }: {
  title: string; onContinue: () => void; onNew: () => void; onGoList: () => void
}) {
  return (
    <div className="relative isolate flex min-h-screen items-center justify-center overflow-hidden bg-[#FAFAFF] pt-24 pb-16">
      <img src={applyBg} alt="" aria-hidden="true" className="pointer-events-none absolute inset-0 h-full w-full object-cover" />
      <div className="relative mx-auto max-w-md px-4 text-center">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full border border-[#E2E4EA] bg-white">
          <Save className="h-7 w-7 text-accent" />
        </div>
        <h1 className="mb-2 text-xl font-bold text-text">บันทึกเป็นร่างแล้ว</h1>
        <p className="mb-1 text-sm text-text-muted">
          ประกาศงาน <span className="font-medium text-text">"{title}"</span>
        </p>
        <p className="mb-8 text-xs text-text-muted/80">
          ร่างจะถูกเก็บไว้ในระบบ คุณสามารถกลับมาแก้ไขและเผยแพร่ได้ทุกเมื่อ
        </p>
        <div className="mb-8 inline-flex items-center gap-2 rounded-sm border border-amber-200 bg-amber-50 px-3.5 py-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
          <span className="text-xs font-medium text-amber-700">สถานะ: ร่าง (ยังไม่เผยแพร่)</span>
        </div>
        <div className="flex flex-col gap-2.5">
          <button onClick={onContinue}
            className="rounded-sm bg-accent py-2.5 text-sm font-semibold text-white transition-colors hover:bg-accent2">
            แก้ไขและเผยแพร่ต่อ
          </button>
          <button onClick={onNew}
            className="rounded-sm border border-[#E2E4EA] py-2.5 text-sm font-medium text-text-muted transition-colors hover:bg-white">
            สร้างประกาศงานใหม่
          </button>
          <button onClick={onGoList} className="py-1.5 text-xs text-text-muted/80 transition-colors hover:text-text-muted">
            กลับหน้า Employers →
          </button>
        </div>
      </div>
    </div>
  )
}

function PublishedScreen({ title, onNew }: { title: string; onNew: () => void }) {
  const navigate = useNavigate()
  return (
    <div className="relative isolate flex min-h-screen items-center justify-center overflow-hidden bg-[#FAFAFF] pt-24 pb-16">
      <img src={applyBg} alt="" aria-hidden="true" className="pointer-events-none absolute inset-0 h-full w-full object-cover" />
      <div className="relative mx-auto max-w-md px-4 text-center">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-accent">
          <CheckCircle2 className="h-7 w-7 text-white" />
        </div>
        <h1 className="mb-2 text-xl font-bold text-text">เผยแพร่ประกาศงานสำเร็จ</h1>
        <p className="mb-8 text-sm text-text-muted">
          ประกาศงาน <span className="font-medium text-accent">"{title}"</span> เผยแพร่บนเว็บไซต์แล้ว
          ผู้หางานสามารถเห็นและสมัครได้ทันที
        </p>
        <div className="flex flex-col gap-2.5">
          <button onClick={onNew}
            className="rounded-sm bg-accent py-2.5 text-sm font-semibold text-white transition-colors hover:bg-accent2">
            สร้างประกาศงานใหม่
          </button>
          <button onClick={() => navigate('/employers')}
            className="rounded-sm border border-[#E2E4EA] py-2.5 text-sm text-text-muted transition-colors hover:bg-white">
            กลับหน้า Employers
          </button>
        </div>
      </div>
    </div>
  )
}

// รอการยืนยันตัวตน — แสดงแทนฟอร์มโพสต์งานถ้าบริษัทยังไม่ผ่านการตรวจสอบจาก admin
function PendingVerificationScreen() {
  const navigate = useNavigate()
  return (
    <div className="relative isolate flex min-h-screen items-center justify-center overflow-hidden bg-[#FAFAFF] pt-24 pb-16">
      <img src={applyBg} alt="" aria-hidden="true" className="pointer-events-none absolute inset-0 h-full w-full object-cover" />
      <div className="relative mx-auto max-w-md px-4 text-center">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full border border-amber-200 bg-amber-50">
          <Clock3 className="h-7 w-7 text-amber-500" />
        </div>
        <h1 className="mb-2 text-xl font-bold text-text">รอการยืนยันตัวตน</h1>
        <p className="mb-8 text-sm text-text-muted">
          บัญชีบริษัทของคุณยังไม่ผ่านการตรวจสอบจากทีมงาน กรุณารอการยืนยันก่อนจึงจะสามารถโพสประกาศงานได้
        </p>
        <button onClick={() => navigate('/employers')}
          className="rounded-sm border border-[#E2E4EA] bg-white px-5 py-2.5 text-sm font-medium text-text-muted transition-colors hover:bg-[#F7F7FA]">
          กลับหน้า Employers
        </button>
      </div>
    </div>
  )
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function PostJobPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const editJobId = Number(searchParams.get('edit')) || null
  const isEditMode = editJobId !== null

  const [form, setForm] = useState<JobForm>({ ...INITIAL_FORM })
  const [errors, setErrors] = useState<Partial<Record<keyof JobForm, string>>>({})
  const [screen, setScreen] = useState<'form' | 'draft' | 'published'>('form')
  const [savedTitle, setSavedTitle] = useState('')
  const [categories, setCategories] = useState<ApiJobCategory[]>([])
  const [skillTags, setSkillTags] = useState<ApiSkillTag[]>([])
  const [workTypeOptions, setWorkTypeOptions] = useState<string[]>([])
  const [expLevelOptions, setExpLevelOptions] = useState<string[]>([])
  // ชนิดคำถามเพิ่มเติมจาก enum ScreeningQuestionType (/api/enums) — โหลดไม่ได้ใช้ลำดับของป้ายภาษาไทยแทน
  const [screeningTypeOptions, setScreeningTypeOptions] = useState<ScreeningType[]>(
    Object.keys(SCREENING_TYPE_LABELS) as ScreeningType[],
  )
  const [submitError, setSubmitError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [company, setCompany] = useState<CompanyProfile | null>(null)
  const isVerified = company === null ? null : company.is_verified   // null = กำลังโหลด
  // โหมดแก้ไข: สถานะปัจจุบันของประกาศ (DRAFT มีปุ่มเผยแพร่เพิ่ม / PAUSED = ถูกระงับ แก้ไม่ได้) + error ตอนโหลด
  const [editStatus, setEditStatus] = useState<string | null>(null)
  const [editLoadError, setEditLoadError] = useState('')

  useEffect(() => {
    getJobCategories().then(setCategories).catch(() => setCategories([]))
    getSkillTags().then(setSkillTags).catch(() => setSkillTags([]))
    getEnums().then(data => {
      setWorkTypeOptions(data.WorkType || [])
      setExpLevelOptions(data.ExperienceLevel || [])
      if (data.ScreeningQuestionType?.length) setScreeningTypeOptions(data.ScreeningQuestionType as ScreeningType[])
    }).catch(() => { setWorkTypeOptions([]); setExpLevelOptions([]) })
    getProfile().then(p => setCompany(p as CompanyProfile))
      .catch(() => setCompany({ is_verified: false } as CompanyProfile))
  }, [])

  // โหมดแก้ไข: เติมข้อมูลเดิมทั้งหมดของประกาศลงฟอร์ม (ได้ทุกสถานะ รวมร่าง/ปิดรับสมัคร)
  useEffect(() => {
    if (!editJobId) return
    getMyJobDetail(editJobId)
      .then(detail => {
        setEditStatus(detail.status)
        setForm(p => ({
          ...p,
          title: detail.title,
          category_id: String(detail.category_id),
          work_type: detail.work_type,
          experience_level: detail.experience_level,
          location: detail.location,
          is_remote: detail.is_remote,
          salary_min: detail.salary_min != null ? String(detail.salary_min) : '',
          salary_max: detail.salary_max != null ? String(detail.salary_max) : '',
          currency: detail.currency,
          is_urgent: detail.is_urgent,
          expires_at: detail.expires_at ? detail.expires_at.slice(0, 10) : '',
          description: detail.description || '',
          requirements: parseJsonList(detail.requirements),
          nice_to_have: parseJsonList(detail.nice_to_have),
          benefits: detail.benefits ?? [],
          tag_ids: detail.tag_ids ?? [],
          screening_questions: (detail.screening_questions ?? []).map((q, i) => ({
            key: i + 1, id: q.id, type: q.type, question: q.question, required: q.required,
            options: q.options?.length ? q.options : ['', ''],
          })),
        }))
      })
      .catch(err => setEditLoadError(err instanceof Error ? err.message : 'โหลดประกาศงานไม่สำเร็จ'))
  }, [editJobId])

  const set = (k: keyof JobForm, v: any) => {
    setForm(p => ({ ...p, [k]: v }))
    setErrors(p => ({ ...p, [k]: undefined }))
  }

  const addReq = (v: string) => set('requirements', [...form.requirements, v])
  const removeReq = (i: number) => set('requirements', form.requirements.filter((_, idx) => idx !== i))
  const addNice = (v: string) => set('nice_to_have', [...form.nice_to_have, v])
  const removeNice = (i: number) => set('nice_to_have', form.nice_to_have.filter((_, idx) => idx !== i))
  const addBenefit = (v: string) => set('benefits', [...form.benefits, v])
  const removeBenefit = (i: number) => set('benefits', form.benefits.filter((_, idx) => idx !== i))
  const toggleSkill = (tagId: number) =>
    set('tag_ids', form.tag_ids.includes(tagId) ? form.tag_ids.filter(id => id !== tagId) : [...form.tag_ids, tagId])

  const validate = () => {
    const e: typeof errors = {}
    if (!form.title.trim()) e.title = 'กรุณากรอกชื่อตำแหน่งงาน'
    if (!form.category_id) e.category_id = 'กรุณาเลือกหมวดหมู่งาน'
    if (!form.location.trim()) e.location = 'กรุณากรอกสถานที่ทำงาน'
    if (!form.description.trim()) e.description = 'กรุณากรอกรายละเอียดงาน'
    if (form.salary_min && form.salary_max && parseFloat(form.salary_min) > parseFloat(form.salary_max)) {
      e.salary_max = 'เงินเดือนสูงสุดต้องมากกว่าขั้นต่ำ'
    }
    const questionError = validateQuestions(form.screening_questions)
    if (questionError) e.screening_questions = questionError
    setErrors(e)
    return Object.keys(e).length === 0
  }

  // ข้อมูลที่ส่งให้ backend — ใช้ร่วมกันทั้งตอนสร้าง (POST /jobs) และแก้ไข (PUT /my-jobs/:id)
  const buildPayload = (): CreateJobPayload => ({
    title: form.title.trim(),
    category_id: Number(form.category_id),
    work_type: form.work_type,
    experience_level: form.experience_level,
    location: form.location.trim(),
    is_remote: form.is_remote,
    salary_min: form.salary_min ? Number(form.salary_min) : null,
    salary_max: form.salary_max ? Number(form.salary_max) : null,
    currency: form.currency,
    description: form.description.trim(),
    requirements: form.requirements.length ? JSON.stringify(form.requirements) : null,
    nice_to_have: form.nice_to_have.length ? JSON.stringify(form.nice_to_have) : null,
    is_urgent: form.is_urgent,
    expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
    tag_ids: form.tag_ids,
    screening_questions: toScreeningPayload(form.screening_questions),
    benefits: form.benefits.length ? form.benefits : null,
  })

  const submitJob = async (status: 'DRAFT' | 'ACTIVE') => {
    setSubmitError('')
    setIsSubmitting(true)
    try {
      await createJob({ ...buildPayload(), status })
      return true
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'เผยแพร่ประกาศงานไม่สำเร็จ')
      return false
    } finally {
      setIsSubmitting(false)
    }
  }

  // บริษัทที่ยืนยันแล้วเผยแพร่ได้ทันที ไม่ต้องรอแอดมินอนุมัติรายประกาศ (ตั้งแต่ 2026-09-28)
  // "บันทึกเป็นร่าง" → status DRAFT (ยังไม่ขึ้นเว็บ), "เผยแพร่" → status ACTIVE (ขึ้นเว็บทันที)
  const handleDraft = async () => {
    if (!form.title.trim()) { setSubmitError('กรุณากรอกชื่อตำแหน่งงานก่อนบันทึก'); return }
    // ร่างก็ต้องผ่านการตรวจคำถามเพิ่มเติม เพราะ backend บันทึกคำถามพร้อมประกาศเลย
    const questionError = validateQuestions(form.screening_questions)
    if (questionError) { setErrors(p => ({ ...p, screening_questions: questionError })); return }
    const ok = await submitJob('DRAFT')
    if (!ok) return
    setSavedTitle(form.title || 'ไม่มีชื่อตำแหน่ง')
    setScreen('draft')
  }

  const handlePublish = async () => {
    if (!validate()) return
    const ok = await submitJob('ACTIVE')
    if (!ok) return
    setSavedTitle(form.title)
    setScreen('published')
  }

  const resetForm = () => {
    setForm({ ...INITIAL_FORM })
    setErrors({})
    setSubmitError('')
    setScreen('form')
  }

  const continueDraft = () => setScreen('form')

  // โหมดแก้ไข: บันทึกจริง (PUT /api/my-jobs/:id) มีผลทันที ไม่ต้องอนุมัติ — publish=true ใช้กับร่าง
  // (บันทึกแล้วเผยแพร่ต่อทันทีด้วย PATCH status) เสร็จแล้วกลับหน้ารายการพร้อมข้อความแจ้งผล
  const handleSaveEdit = async (publish: boolean) => {
    if (!validate() || !editJobId) return
    setSubmitError('')
    setIsSubmitting(true)
    try {
      const saved = await updateMyJob(editJobId, buildPayload())
      if (publish) await setMyJobStatus(editJobId, 'ACTIVE')
      const savedMessage = publish ? `บันทึกและเผยแพร่ "${saved.title}" แล้ว` : `บันทึกการแก้ไข "${saved.title}" แล้ว`
      navigate('/account?tab=job-postings', { state: { savedMessage } })
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'บันทึกการแก้ไขไม่สำเร็จ')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (screen === 'draft') {
    return <DraftSavedScreen title={savedTitle} onContinue={continueDraft} onNew={resetForm} onGoList={() => navigate('/employers')} />
  }
  if (screen === 'published') {
    return <PublishedScreen title={savedTitle} onNew={resetForm} />
  }

  if (isVerified === null) {
    return <div className="flex min-h-screen items-center justify-center bg-[#F8F9FC] pt-24 pb-16" />
  }
  if (isVerified === false) {
    return <PendingVerificationScreen />
  }

  const categoryName = categories.find(c => String(c.job_category_id) === form.category_id)?.name || ''
  const backTarget = '/account?tab=job-postings'

  return (
    // หน้านี้เนื้อหายาวมาก (ฟอร์มหลายส่วน) สูงกว่า viewport เยอะ — ถ้าใช้ <img h-full w-full object-cover>
    // เหมือนหน้าอื่น รูปจะถูกยืดตามความสูงทั้งหน้า (ไม่ใช่แค่ viewport) จนภาพผิดเพี้ยน เห็นแค่จุดจางๆ ไม่เห็นลายจริง
    // เปลี่ยนมาใช้ CSS background-image แบบ bg-fixed แทน — ขนาด/ตำแหน่งรูปอิงจาก viewport เสมอ ไม่ว่าหน้าจะยาวแค่ไหน
    // ได้ผลลัพธ์เหมือนหน้าสั้นๆ อื่นๆ (เช่น JobApplication) เป๊ะ
    <div
      className="min-h-screen bg-[#FAFAFF] bg-cover bg-top bg-fixed pt-24 pb-16"
      style={{ backgroundImage: `url(${applyBg})` }}
    >
      <div className="relative mx-auto max-w-[1400px] px-4 md:px-8">

        {/* ── Page header ── */}
        <button onClick={() => navigate(backTarget)} className="mb-4 flex items-center gap-1.5 text-sm text-text-muted transition-colors hover:text-text">
          <ArrowLeft className="h-3.5 w-3.5" /> กลับไปหน้าจัดการงาน
        </button>

        <div className="mb-7 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-display text-[28px] font-bold text-text sm:text-[32px]">
              {isEditMode ? 'แก้ไขประกาศงาน' : 'สร้างประกาศงานใหม่'}
            </h1>
            <p className="mt-2 text-sm text-text-muted">
              {isEditMode ? 'ข้อมูลจากประกาศเดิมถูกเติมให้อัตโนมัติ — แก้ไขแล้วบันทึกได้เลย' : 'กรอกข้อมูลตำแหน่งงานเพื่อค้นหาผู้สมัครที่เหมาะสม'}
            </p>
          </div>

          {!isEditMode && (
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 rounded-sm border border-emerald-200 bg-emerald-50 px-3.5 py-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                <div>
                  <p className="text-xs font-semibold text-emerald-700">บันทึกอัตโนมัติแล้ว</p>
                  <p className="text-[10px] text-emerald-600/80">เมื่อสักครู่</p>
                </div>
              </div>
              <button onClick={handleDraft} disabled={isSubmitting}
                className="flex items-center gap-2 rounded-sm border border-[#E2E4EA] bg-white px-4 py-2.5 text-sm font-medium text-text-muted transition-colors hover:bg-[#F8F9FC] disabled:opacity-50">
                <Save className="h-4 w-4" /> บันทึกเป็นร่าง
              </button>
            </div>
          )}
        </div>

        {isEditMode && editLoadError && (
          <div className="mb-6 flex items-center gap-2 rounded-sm border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-600">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {editLoadError}
          </div>
        )}
        {isEditMode && editStatus === 'PAUSED' && (
          <div className="mb-6 flex items-center gap-2 rounded-sm border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            ประกาศนี้ถูกระงับโดยทีมงาน จึงแก้ไขไม่ได้ กรุณาติดต่อทีมงานหากต้องการเปิดใหม่
          </div>
        )}

        {/* ── 2-column layout: form (ซ้าย) + live preview (ขวา) ── */}
        <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">

          {/* ── ซ้าย: ฟอร์ม ── */}
          <div className="rounded-sm border border-[#E2E4EA] bg-white px-6 py-2 shadow-[0_1px_3px_rgba(20,20,43,0.04)] sm:px-8">

            {/* ข้อมูลตำแหน่งงาน + เงินเดือน (รวมเป็นชุดเดียวกัน) */}
            <div className="py-8">
              <SectionHeading icon={ClipboardList} title="ข้อมูลตำแหน่งงาน" description="ระบุข้อมูลพื้นฐานของตำแหน่งงาน" />
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Field label="ชื่อตำแหน่งงาน" required error={errors.title}>
                    <Input value={form.title} onChange={e => set('title', e.target.value)} placeholder="เช่น Senior Frontend Developer" />
                  </Field>
                </div>

                <Field label="หมวดหมู่งาน" required error={errors.category_id}>
                  <Select value={form.category_id} onChange={e => set('category_id', e.target.value)}>
                    <option value="">เลือกหมวดหมู่...</option>
                    {categories.map(c => <option key={c.job_category_id} value={c.job_category_id}>{c.name}</option>)}
                  </Select>
                </Field>

                <Field label="สถานที่ทำงาน" required error={errors.location}>
                  <Input value={form.location} onChange={e => set('location', e.target.value)} placeholder="เช่น กรุงเทพฯ, เชียงใหม่" />
                </Field>

                <div className="sm:col-span-2">
                  <Field label="รูปแบบการทำงาน">
                    <SegmentedGroup
                      options={workTypeOptions.map(value => ({ value, label: WORK_TYPE_LABELS[value] ?? value }))}
                      value={form.work_type}
                      onChange={v => set('work_type', v)}
                    />
                  </Field>
                </div>

                <div className="sm:col-span-2">
                  <Field label="ระดับประสบการณ์">
                    <SegmentedGroup
                      options={expLevelOptions.map(value => ({ value, label: EXP_LABELS[value] ?? value }))}
                      value={form.experience_level}
                      onChange={v => set('experience_level', v)}
                    />
                  </Field>
                </div>

                <div className="sm:col-span-2">
                  <SettingRow label="เปิดรับสมัครแบบ Remote" description="ผู้สมัครสามารถทำงานนอกสถานที่ได้">
                    <Switch checked={form.is_remote} onChange={() => set('is_remote', !form.is_remote)} />
                  </SettingRow>
                </div>

                <div className="sm:col-span-2 border-t border-[#E2E4EA] pt-6">
                  <SubHeading icon={Wallet} title="เงินเดือน" />
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_1fr_120px]">
                    <Field label="เงินเดือนขั้นต่ำ" hint="เว้นว่างได้ถ้าไม่ระบุ">
                      <Input type="number" min={0} value={form.salary_min} onChange={e => set('salary_min', e.target.value)} placeholder="30,000" />
                    </Field>
                    <Field label="เงินเดือนสูงสุด" error={errors.salary_max} hint={errors.salary_max ? undefined : 'เว้นว่างได้ถ้าไม่ระบุ'}>
                      <Input type="number" min={0} value={form.salary_max} onChange={e => set('salary_max', e.target.value)} placeholder="60,000" />
                    </Field>
                    <Field label="สกุลเงิน">
                      <Select value={form.currency} onChange={e => set('currency', e.target.value)}>
                        {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </Select>
                    </Field>
                  </div>
                </div>
              </div>
            </div>

            <Divider />

            {/* รายละเอียดงาน */}
            <div className="py-8">
              <SectionHeading icon={FileText} title="รายละเอียดและคุณสมบัติ" description="อธิบายงานและคุณสมบัติที่ต้องการจากผู้สมัคร" />
              <div className="space-y-7">
                <Field label="รายละเอียดงาน" required error={errors.description}>
                  <Textarea rows={6} value={form.description} onChange={e => set('description', e.target.value)}
                    placeholder="อธิบายหน้าที่ความรับผิดชอบ ทีมงาน และสภาพแวดล้อมการทำงาน..." />
                </Field>

                <Field label="คุณสมบัติผู้สมัคร" hint="เพิ่มคุณสมบัติทีละข้อ เช่น มีประสบการณ์ React อย่างน้อย 2 ปี">
                  <DynamicList items={form.requirements} onAdd={addReq} onRemove={removeReq}
                    placeholder="เช่น มีประสบการณ์ React ≥ 2 ปี" />
                </Field>

                <Field label="คุณสมบัติที่พิจารณาเป็นพิเศษ" hint="ไม่บังคับ — เพิ่มทีละข้อ เช่น มีประสบการณ์ทำงานกับทีม Agile">
                  <DynamicList items={form.nice_to_have} onAdd={addNice} onRemove={removeNice}
                    placeholder="เช่น เคยใช้ AWS มาก่อน" />
                </Field>

                <Field label="สวัสดิการ" hint="ไม่บังคับ — เพิ่มทีละข้อ เช่น ประกันสังคม, โบนัสประจำปี">
                  <DynamicList items={form.benefits} onAdd={addBenefit} onRemove={removeBenefit}
                    placeholder="เช่น ประกันสังคม" />
                </Field>
              </div>
            </div>

            <Divider />

            {/* ทักษะที่ต้องการ */}
            <div className="py-8">
              <SectionHeading icon={Sparkles} title="ทักษะที่ต้องการ" description="ไม่บังคับ — เลือกทักษะที่เกี่ยวข้องกับตำแหน่งนี้" />
              <SkillPicker options={skillTags} selected={form.tag_ids} onToggle={toggleSkill} />
            </div>

            <Divider />

            {/* คำถามเพิ่มเติมถึงผู้สมัคร */}
            <div className="py-8">
              <SectionHeading icon={MessageSquareText} title="คำถามเพิ่มเติมถึงผู้สมัคร"
                description={`ไม่บังคับ — ถามข้อมูลเฉพาะตำแหน่งนี้ได้สูงสุด ${MAX_SCREENING_QUESTIONS} ข้อ ผู้สมัครตอบตอนยื่นใบสมัคร`} />
              <ScreeningQuestionsEditor
                questions={form.screening_questions}
                typeOptions={screeningTypeOptions}
                onChange={qs => set('screening_questions', qs)}
              />
              {errors.screening_questions && <p className="mt-3 text-xs text-rose-500">{errors.screening_questions}</p>}
            </div>

            <Divider />

            {/* การเผยแพร่ */}
            <div className="py-8">
              <SectionHeading icon={Megaphone} title="การเผยแพร่" />
              <div className="divide-y divide-[#E2E4EA]">
                <SettingRow label="ประกาศงานด่วน" description="แสดงป้าย “ด่วน” ให้ผู้สมัครเห็นเด่นชัดขึ้น">
                  <Switch checked={form.is_urgent} onChange={() => set('is_urgent', !form.is_urgent)} />
                </SettingRow>
                <SettingRow label="วันหมดอายุประกาศ" description="เว้นว่างได้ถ้าไม่กำหนด">
                  <Input type="date" value={form.expires_at} onChange={e => set('expires_at', e.target.value)} className="w-[180px]" />
                </SettingRow>
              </div>
            </div>

            {Object.values(errors).some(Boolean) && (
              <div className="mb-8 flex items-center gap-2 rounded-sm border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-600">
                <AlertCircle className="h-4 w-4 shrink-0" />
                กรุณาตรวจสอบข้อมูลที่ไม่ครบถ้วนด้านบน
              </div>
            )}
            {submitError && (
              <div className="mb-8 flex items-center gap-2 rounded-sm border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-600">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {submitError}
              </div>
            )}
          </div>

          {/* ── ขวา: ตัวอย่างประกาศ (sticky) ── */}
          <div className="lg:sticky lg:top-24">
            <JobPreviewPanel company={company} form={form} categoryName={categoryName} skillTags={skillTags} />
          </div>
        </div>

        {/* ── Bottom action bar ── */}
        <div className="mt-6 flex items-center justify-between gap-3 rounded-sm border border-[#E2E4EA] bg-white px-6 py-5">
          <button onClick={() => navigate(backTarget)}
            className="flex items-center justify-center gap-2 rounded-sm border border-[#E2E4EA] bg-white px-5 py-2.5 text-sm font-medium text-text-muted transition-colors hover:bg-[#F8F9FC]">
            ยกเลิก
          </button>

          {isEditMode ? (
            <div className="flex items-center gap-3">
              {/* ร่าง: บันทึกไว้ก่อน หรือบันทึกแล้วเผยแพร่ทันที / สถานะอื่น: บันทึกอย่างเดียว (มีผลทันที) */}
              <button onClick={() => handleSaveEdit(false)} disabled={isSubmitting || !editStatus || editStatus === 'PAUSED'}
                className={editStatus === 'DRAFT'
                  ? 'flex items-center justify-center gap-2 rounded-sm border border-[#E2E4EA] bg-white px-5 py-2.5 text-sm font-medium text-text-muted transition-colors hover:bg-[#F8F9FC] disabled:opacity-50'
                  : 'flex items-center justify-center gap-2 rounded-sm bg-accent px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-accent2 disabled:opacity-50'}>
                <Save className="h-4 w-4" /> {editStatus === 'DRAFT' ? 'บันทึกร่าง' : 'บันทึกการเปลี่ยนแปลง'}
              </button>
              {editStatus === 'DRAFT' && (
                <button onClick={() => handleSaveEdit(true)} disabled={isSubmitting}
                  className="flex items-center justify-center gap-2 rounded-sm bg-accent px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-accent2 disabled:opacity-50">
                  {isSubmitting ? 'กำลังบันทึก...' : 'บันทึกและเผยแพร่'} <Send className="h-4 w-4" />
                </button>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <button onClick={handleDraft} disabled={isSubmitting}
                className="flex items-center justify-center gap-2 rounded-sm border border-[#E2E4EA] bg-white px-5 py-2.5 text-sm font-medium text-text-muted transition-colors hover:bg-[#F8F9FC] disabled:opacity-50">
                <Save className="h-4 w-4" /> บันทึกเป็นร่าง
              </button>
              {/* หมายเหตุ: ปุ่มนี้ยังคงเรียก handlePublish เดิม (เผยแพร่ทันที ไม่มีขั้นถัดไปจริง) —
                  ใช้ข้อความ "ถัดไป →" ตามดีไซน์อ้างอิงเท่านั้น ไม่ได้เปลี่ยนพฤติกรรมการทำงาน */}
              <button onClick={handlePublish} disabled={isSubmitting}
                className="flex items-center justify-center gap-2 rounded-sm bg-accent px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-accent2 disabled:opacity-50">
                {isSubmitting ? 'กำลังเผยแพร่...' : 'ถัดไป'} <Send className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
